import { ApolloError } from 'apollo-client';
import React from 'react';
import styled from 'styled-components';
import { path, pathOr } from 'rambda';
import TagManager from 'react-gtm-module';
import { GraphQLError } from 'graphql';

import logger from '@williamhill/rosi-ui-shared/src/lib/logger';
import { getDeviceInfo } from '@williamhill/rosi-ui-shared/src/lib/device-info';
import { showFailureModal, showNotFoundModal } from '@williamhill/rosi-ui-shared/src/components/modal';
import { badRequest } from '@williamhill/rosi-ui-shared/src/components/modal/error-modals';
import { handlePlaceBetUnprocessableEntity } from '@williamhill/rosi-ui-shared/src/lib/bet-errors';
import { logout } from '@williamhill/rosi-ui-shared/src/auth';
import { formatMoneyWithSymbol } from '@williamhill/rosi-ui-shared/src/lib/money-formatter';
import { unitsValueMoney } from '@williamhill/rosi-ui-shared/src/lib/money';
import { fireGtmEvent, hasSgpBet } from '@williamhill/rosi-ui-shared/src/lib';
import { Text } from '../components/typography';
import { showRewardsCardModal, getRewardsModalTitle, showAnonymousKioskAmountExceededModal } from '../rewards-program';
import { CARD_ERROR_CODES } from '../rewards-program/card-error-codes';

export interface IErrorDetails {
  code: string;
  exception: {
    code?: string;
    description?: string;
    title?: string;
    details: any;
  };
  title?: string;
  details?: any;
}

export interface IGraphqlErrorResponse {
  message: string;
  extensions: IErrorDetails;
}

export type IGraphqlErrors = {
  graphQLErrors: IGraphqlErrorResponse[];
};

export const getRootCause = (error?: IGraphqlErrors | ApolloError): IErrorDetails =>
  path(['graphQLErrors', 0, 'extensions'], error);

/*
  Note to devs:
  when adding new error text descriptors it is vital the operationIdentifier you put
  into the below operation text objects match up with the operationName on your
  graphQl request.

  eg: findBet

  the GQL query looks as follows:

    query findBet($id: String!) {
    bet(id: $id) {

  so it follows the pattern:

    <gql operation> <operationName>(arguments){
      <graphql operation from schema>(arguments){
        ...
      }
    }
*/
interface IErrorHandler {
  error: IErrorDetails;
  operationIdentifier: string;
  operationVariables: any;
}

const unauthenticated = ({ url }) => {
  logout({ graphqlUrl: url });
};

const messageResolvers = {
  'MONEY-004': (error: IErrorDetails) => {
    const maxTransferAmount: number = path(['exception', 'details', 'maxTransfer'], error);
    if (maxTransferAmount) {
      const formattedAmount = formatMoneyWithSymbol(
        unitsValueMoney(maxTransferAmount),
        getDeviceInfo().currency.symbol
      );
      return `The maximum transfer amount allowed is ${formattedAmount}. Check value and try again.`;
    }
    return 'The requested operation is not permitted. Check details and try again.';
  },
  'MONEY-005': (error: IErrorDetails) => {
    return error.exception.description;
  }
};

export const resolveErrorMessage = (error: IErrorDetails) => {
  if (error.code && error.code in messageResolvers) {
    return messageResolvers[error.code](error);
  }
  return pathOr('The request could not be completed because of an error', ['exception', 'description'], error);
};

const notFound = ({ operationIdentifier }: IErrorHandler) => {
  const notFoundOperationText = {
    bets: {
      title: 'Bet Not Found',
      context: 'The bet you were looking for does not exist'
    },
    default: {
      title: 'Not Found',
      context: 'Entity not found'
    }
  };
  const operationText = notFoundOperationText[operationIdentifier] || notFoundOperationText.default;
  return showNotFoundModal({
    actionAlign: 'center',
    title: operationText.title,
    context: operationText.context,
    width: 'default'
  });
};

const conflict = ({ error }: IErrorHandler) => {
  showFailureModal({
    title: 'Unable to complete transaction',
    context: resolveErrorMessage(error)
  });
};

const showUnprocessableEntityFailureModal = ({ operationIdentifier }: IErrorHandler) => {
  const unprocessableEntityOperationText = {
    voidBet: {
      title: 'Unable to Void Bet',
      content: <Message>This bet cannot be voided</Message>,
      context: 'Try again later or contact customer support.'
    },
    payoutBet: {
      title: 'Unable to Payout Bet',
      content: <Message>This bet cannot be paid out</Message>,
      context: 'This may be because the bet is awaiting settlement. Try again later or contact customer support.'
    },
    default: {
      title: 'Unprocessable Entity',
      content: '',
      context: 'The request could not be completed'
    }
  };
  const operationText =
    unprocessableEntityOperationText[operationIdentifier] || unprocessableEntityOperationText.default;
  return showFailureModal({
    title: operationText.title,
    content: operationText.content,
    context: operationText.context
  });
};

const unprocessableEntity = (props: IErrorHandler) => {
  if (props.operationIdentifier === 'placeBets') {
    handlePlaceBetUnprocessableEntity(props.error, props.operationVariables);
  } else {
    showUnprocessableEntityFailureModal(props);
  }
};

const forbidden = ({ error, operationIdentifier }: IErrorHandler) => {
  const { slotId, currency, manualRewardEntry, playerCardProvider, rewardsModal } = getDeviceInfo();
  const code = error?.code;

  if (slotId.type === 'POS' && code === 'BETTING-008') {
    // Supress error - supervisor auth modal will be rendered instead
    return;
  }

  if (operationIdentifier === 'placeBets') {
    const reason = error.exception?.details?.reasons.find((r: any) => r.reason === 'EXCEEDS_MAX_ANON_USER_STAKE');
    if (reason && slotId.type === 'KIOSK') {
      const kioskAnonymousLimitAmount = formatMoneyWithSymbol(unitsValueMoney(reason.detail.limit), currency.symbol);

      if (!playerCardProvider) {
        showAnonymousKioskAmountExceededModal({
          title: 'Maximum Wager Amount Exceeded',
          kioskAnonymousLimitAmount,
          action: 'wager amount',
          context: 'on this Kiosk'
        });
        return;
      }

      showRewardsCardModal({
        title: getRewardsModalTitle({
          rewardsModalConfiguration: rewardsModal,
          isManualEntryEnabled: manualRewardEntry?.enabled,
          key: 'unableToPlaceBet'
        }),
        type: 'warning',
        kioskAnonymousLimitAmount,
        playerCardProvider,
        isManualEntryEnabled: manualRewardEntry?.enabled
      });
      return;
    }
  }
  showFailureModal({
    title: 'Unable to complete transaction',
    context: resolveErrorMessage(error)
  });
};

const systemError = (errors: { message: string }[] | IErrorHandler) => {
  const title = 'System Error';

  const message = Array.isArray(errors)
    ? errors
        .filter(Boolean)
        .map(({ message }) => message)
        .join(', ')
    : errors.error.code;

  logger.error(message);

  TagManager.dataLayer({
    dataLayer: {
      event: 'global-system-error',
      globalError: {
        name: title,
        message
      }
    },
    dataLayerName: 'USROSI'
  });

  if (message === 'PIGS-001') {
    return;
  }

  showFailureModal({
    title,
    context: 'There has been a problem with the system. If the problem reoccurs, report to a member of staff.'
  });
};

const graphqlResponseErrorCodesMap = {
  403: forbidden,
  404: notFound,
  409: conflict,
  422: unprocessableEntity,
  400: unprocessableEntity, // Liberty returns 400 where WHBE returned 422 (it shouldn't, so not renaming yet)
  502: systemError
};

export const handleGraphqlResponseErrors = ({
  error,
  onAuthenticationError,
  operationIdentifier,
  operationVariables,
  url
}: {
  error: IGraphqlErrorResponse[];
  onAuthenticationError?(): void;
  operationIdentifier: string;
  operationVariables: any;
  url: string;
}) => {
  const [firstError] = error;

  if (
    ['login', 'getAllSports', 'addToBetslipEvent', 'NavigationItems', 'parlayHubQuickPicks'].includes(
      operationIdentifier
    )
  ) {
    return;
  }

  const errorType = pathOr('UNKNOWN', ['extensions', 'code'], firstError);

  if (errorType.startsWith('VOUCHER')) {
    return;
  }
  if (errorType === CARD_ERROR_CODES.SERVICE_UNAVAILABLE || errorType === CARD_ERROR_CODES.HTTP_503) {
    return;
  }
  if (['UNAUTHENTICATED', 'SECURITY-005', 'SECURITY-006'].includes(errorType)) {
    if (onAuthenticationError) {
      onAuthenticationError();
    }
    return unauthenticated({ url });
  }
  if (['FORBIDDEN'].includes(errorType)) {
    return;
  }

  // Specific patch to prevent global error when errors are returned for sgp on bettingOpportunities
  // TODO: Expectation is this will be removed when ROSI-11416 is implemented
  if (errorType === 'INTERNAL_SERVER_ERROR' && operationIdentifier === 'bettingOpportunities') {
    return;
  }

  // ROSI-7917 This will be retried upto 10 times and the error message is handled elsewhere.
  if (pathOr(null, [0, 'extensions', 'code'], error) === 'BETTING-004' && operationIdentifier === 'bets') {
    return;
  }

  // Specific patch to prevent the error occurring on the search page.
  const httpStatusPath = pathOr(502, ['path'], firstError);
  if (httpStatusPath.length && (httpStatusPath.includes('searchSummary') || httpStatusPath.includes('search'))) {
    return;
  }

  const httpStatusCode = pathOr(502, ['extensions', 'exception', 'code'], firstError);
  const errorCodeToUse = Object.keys(graphqlResponseErrorCodesMap).includes(httpStatusCode.toString())
    ? httpStatusCode
    : 502;

  // ROSI-6077 Specific patch as we want to avoid the popup for an error that isn't real
  if (operationIdentifier === 'bettingOpportunities' && errorCodeToUse === 400) {
    return;
  }

  if (operationIdentifier === 'placeBets' && hasSgpBet(operationVariables.bets)) {
    fireGtmEvent('placed-bet-error-sgp', { errorCode: errorType });
  }

  if (httpStatusPath?.[0] === 'eventsForHomepage' && firstError?.extensions?.code === 'SDS-002') {
    return;
  }

  graphqlResponseErrorCodesMap[errorCodeToUse]({
    error: firstError.extensions,
    operationIdentifier,
    operationVariables
  });
};

const graphqlHttpCodeHandlers = {
  400: badRequest,
  404: notFound,
  502: systemError
};

export const handleGraphqlServerError = (
  responseHttpStatusCode: string,
  errorMessages: (GraphQLError | Error | undefined)[]
) => {
  const errorCodeToUse = Object.keys(graphqlHttpCodeHandlers).includes(responseHttpStatusCode)
    ? responseHttpStatusCode
    : 502;
  graphqlHttpCodeHandlers[errorCodeToUse](errorMessages);
};

const ApolloErrors = {
  handleGraphqlResponseErrors,
  handleGraphqlServerError
};

export const Message = styled(Text).attrs(() => {
  return { weight: 'bold' };
})`
  margin-top: 2rem;
  display: block;
`;

export default ApolloErrors;
